import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Table,
  message,
  Space,
  Row,
  Col,
  Modal,
  Typography,
  Spin,
  Empty,
  InputNumber,
  Checkbox,
} from "antd";
import { DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import API from "../../api";
import { add, debounce } from "lodash";
import { useSelector } from "react-redux";

const { Text } = Typography;
const { Option } = Select;

const SaleForm = ({ open, setOpen, data, onSave }) => {
  const [form] = Form.useForm();
  const [formCustomer] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [allStocks, setAllStocks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsInSale, setProductsInSale] = useState([]);
  const [user, setUser] = useState("");
  const [saleDiscount, setSaleDiscount] = useState({
    value: 0,
    type: "percentage",
  });
  const [financeMethod, setFinanceMethod] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCustomerModel, setShowCustomerModel] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [showInvoiceModel, setShowInvoiceModel] = useState(false);
  const [saleId, setSaleId] = useState(null);
  const [paidAmount, setPaidAmount] = useState("0.00");
  const [balanceAmount, setBalanceAmount] = useState("0.00");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isPaidAmountEdited, setIsPaidAmountEdited] = useState(false);
  const [cashAmount, setCashAmount] = useState("0.00");
  const [onlineAmount, setOnlineAmount] = useState("0.00");
  const [productFinanceDetails, setProductFinanceDetails] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [saleFormData, setSaleFormData] = useState(null);
  const [trackingNo, setTrackingNo] = useState(null);
  const [batchNumber, setBatchNumber] = useState(null);
  const [trackingNumbers, setTrackingNumbers] = useState([]);
  const [batchNumbers, setBatchNumbers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [pendingInsuranceApproval, setPendingInsuranceApproval] =
    useState(false);

  const [additionalCharges, setAdditionalCharges] = useState({
    delivery: "",
    service: "",
    other: "",
  });

  const [showAdditional, setShowAdditional] = useState(false);

  const productSettings = useSelector(
    (state) => state.productSetting?.productSetting
  );

  const [storeSettings, setStoreSettings] = useState({
    trackingNumber: "No",
    batchNumber: "No",
  });

  useEffect(() => {
    if (paymentMethod === "cash_online") {
      const cash = parseFloat(cashAmount) || 0;
      const online = parseFloat(onlineAmount) || 0;
      const totalPaid = (cash + online).toFixed(2);
      if (cash > 0 || online > 0) {
        setPaidAmount(totalPaid);
        setIsPaidAmountEdited(true);
      }
    }
  }, [cashAmount, onlineAmount, paymentMethod]);

  const fetchData = async () => {
    try {
      const storeResponse = await API.get("/stores");
      setStores(Array.isArray(storeResponse.data) ? storeResponse.data : []);
    } catch (error) {
      message.error("Failed to fetch stores.");
      console.error("Error fetching stores:", error);
    }

    try {
      const customerResponse = await API.get("/customers");
      setCustomers(
        Array.isArray(customerResponse.data) ? customerResponse.data : []
      );
    } catch (error) {
      message.error("Failed to fetch customers.");
      console.error("Error fetching customers:", error);
    }

    try {
      const employeeResponse = await API.get("/employee");
      setEmployees(
        Array.isArray(employeeResponse.data) ? employeeResponse.data : []
      );
    } catch (error) {
      message.error("Failed to fetch employees.");
      console.error("Error fetching employees:", error);
    }
  };

  const fetchTax = async () => {
    try {
      const taxResponse = await API.get("/taxes");
      setTaxes(Array.isArray(taxResponse?.data) ? taxResponse.data : []);
    } catch (error) {
      console.error("Error fetching taxes:", error);
      message.error("Failed to fetch taxes.");
    }
  };

  const fetchUser = useCallback(() => {
    const userData = JSON.parse(localStorage?.getItem("userData"));
    setUser(userData);

    if (userData) {
      if (userData.role === "admin") {
        const settings = productSettings.find(
          (setting) => setting.storeId === selectedStoreId
        );
        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        } else {
          setStoreSettings({ trackingNumber: "No", batchNumber: "No" });
        }
      } else if (userData.storeId) {
        const settings = productSettings.find(
          (setting) => setting.storeId === userData.storeId
        );

        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        }
      }
    }
  }, [selectedStoreId, productSettings]);

  const excludedProductIds = useMemo(
    () => productsInSale.map((item) => item.product.id),
    [productsInSale]
  );

  const fetchProducts = async (
    search = "",
    excludedProductIds = [],
    storeId = ""
  ) => {
    if (!storeId) {
      console.warn("storeId is required to fetch products.");
      return;
    }

    try {
      const params = {
        search: search.trim(),
        storeId,
        page: currentPage,
        limit: pageLimit,
      };

      const res = await API.get("/stocks", { params });
      const stocks = Array.isArray(res?.data?.data?.stocks)
        ? res.data.data.stocks
        : [];

      setStocks(stocks);

      const param = {
        search: search.trim(),
        storeId,
      };

      const response = await API.get("/products/paginate", {
        params: param,
      });

      const products = Array.isArray(response?.data?.products)
        ? response?.data?.products
        : [];

      const productsMap = new Map(products.map((prod) => [prod._id, prod]));

      const productMap = new Map();

      stocks.forEach((stock) => {
        const product = stock.productId;
        const productId = product._id;

        const productDetails = productsMap.get(productId);

        if (!productMap.has(productId)) {
          productMap.set(productId, {
            stockId: stock._id,
            storeId: stock.storeId?._id || stock.storeId,
            storeName: stock.storeId?.name || "",
            product: {
              id: productId,
              name: product.specifications?.name || "Unnamed Product",
            },
            barcode: product.barcode?.code || "",
            hsn_sac: product.hsn_sac || "",
            quantity: stock.quantity,
            batchId: null,
            batchNumber: "",
            trackingNumber: "",
            purchasePrice: 0,
            salePrice: 0,
            mrp: 0,
            taxes: {
              id: "",
              percentage: 0,
            },
            batches:
              stock.batch && Array.isArray(stock.batch) ? stock.batch : [],
            productPricing: productDetails?.pricing || null,
          });
        } else {
          const existingProduct = productMap.get(productId);
          existingProduct.quantity += stock.quantity;
          if (stock.batch && Array.isArray(stock.batch)) {
            existingProduct.batches.push(...stock.batch);
          }
        }
      });

      const enrichedProducts = Array.from(productMap.values()).map(
        (product) => {
          const firstBatch =
            product.batches.length > 0 ? product.batches[0] : null;
          const fallbackPricing = product.productPricing || {};
          const mrp = firstBatch?.mrp || fallbackPricing.mrpPrice || 0;
          const salePrice =
            firstBatch?.sellingPrice || fallbackPricing.salePrice || 0;
          const purchasePrice =
            firstBatch?.purchasePrice || fallbackPricing.purchasePrice || 0;
          const taxes = firstBatch?.gst
            ? {
                id: firstBatch.gst.id || "",
                percentage: firstBatch.gst.percentage || 0,
              }
            : fallbackPricing.taxes &&
              Array.isArray(fallbackPricing.taxes) &&
              fallbackPricing.taxes.length > 0
            ? {
                id: fallbackPricing.taxes[0].taxId || "",
                percentage: fallbackPricing.taxes[0].percentage || 0,
              }
            : { id: "", percentage: 0 };

          return {
            ...product,
            batchId: firstBatch?._id || null,
            batchNumber: firstBatch?.batchNumber || "",
            trackingNumber: firstBatch?.tracking_number || "",
            purchasePrice,
            salePrice,
            mrp,
            taxes,
          };
        }
      );

      setProducts(enrichedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error("Failed to fetch products.");
    }
  };

  const fetchTrackingAndBatchNumbers = useCallback(() => {
    if (!selectedProduct || !selectedStoreId || !Array.isArray(allStocks)) {
      setTrackingNumbers([]);
      setBatchNumbers([]);
      return;
    }

    const stock = allStocks.find(
      (s) =>
        s.storeId?._id === selectedStoreId &&
        s.productId?._id === selectedProduct.product.id
    );

    if (!stock) {
      setTrackingNumbers([]);
      setBatchNumbers([]);
      return;
    }

    if (Array.isArray(stock.batch) && stock.batch.length > 0) {
      const trackingNumbers = [
        ...new Set(
          stock.batch.map((batch) => batch.tracking_number).filter(Boolean)
        ),
      ];
      const batchNumbers = [
        ...new Set(
          stock.batch.map((batch) => batch.batchNumber).filter(Boolean)
        ),
      ];
      setTrackingNumbers(trackingNumbers);
      setBatchNumbers(batchNumbers);
    } else {
      setTrackingNumbers([]);
      setBatchNumbers([]);
    }
  }, [selectedProduct, selectedStoreId, allStocks]);

  const debouncedFetch = useRef(
    debounce((search, excluded, storeId, page, limit) => {
      setLoading(true);
      fetchProducts(search, excluded, storeId, page, limit).finally(() =>
        setLoading(false)
      );
    }, 500)
  ).current;

  const handleDownload = async (id) => {
    try {
      const result = await API.get(`/sales/generate-invoice/${id}`);
      if (result.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${result.data.downloadLink.split("/").pop()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to generate invoice.");
    }
  };

  async function getCityState(pincode) {
    const response = await fetch(
      `https://api.postalpincode.in/pincode/${pincode}`
    );
    const data = await response.json();
    if (data[0].Status === "Success") {
      const postOffice = data[0].PostOffice[0];
      formCustomer.setFieldsValue({
        address: {
          city: postOffice?.District || "",
          state: postOffice?.State || "",
        },
      });
    } else {
      formCustomer.setFieldsValue({ address: { city: null, state: null } });
      message.error("PinCode not found");
    }
  }

  useEffect(() => {
    if (selectedStoreId) {
      fetchProducts(
        "",
        excludedProductIds,
        selectedStoreId,
        currentPage,
        pageLimit
      );
      const fetchAllStocksForChecks = async () => {
        try {
          const params = { storeId: selectedStoreId };
          const res = await API.get("/stocks", { params });
          setAllStocks(
            Array.isArray(res?.data?.data?.stocks) ? res.data.data.stocks : []
          );
        } catch (error) {
          console.error("Error fetching all stocks for checks:", error);
          message.warning(
            "Could not load full stock data for quantity checks."
          );
          setAllStocks([]);
        }
      };
      fetchAllStocksForChecks();
    } else {
      setAllStocks([]);
    }
  }, [excludedProductIds, selectedStoreId, currentPage, pageLimit]);

  useEffect(() => {
    fetchUser();
    fetchData();
    fetchTax();
  }, [fetchUser]);

  useEffect(() => {
    fetchTrackingAndBatchNumbers();
  }, [fetchTrackingAndBatchNumbers]);

  const calculateProductTotal = (product) => {
    const salePrice = product.salePrice || 0;
    let total = salePrice * product.quantity;
    if (product.discount?.value) {
      if (product.discount.type === "percentage") {
        total -= (product.discount.value / 100) * total;
      } else if (product.discount.type === "flat") {
        total -= product.discount.value * product.quantity;
      }
    }
    return parseFloat(total.toFixed(2));
  };

  const handleSubmit = async (values) => {
    if (!selectedStoreId) {
      message.error("Please select a store.");
      return;
    }

    if (productsInSale.length === 0) {
      message.error("Please add at least one product to the sale.");
      return;
    }

    const productsWithoutTax = productsInSale.filter(
      (item) =>
        !item.taxes || !item.taxes.id || item.taxes.percentage === undefined
    );

    if (productsWithoutTax.length > 0) {
      message.error("Please select a tax for the products in the sale.");
      return;
    }

    const productsWithZeroPurchasePrice = productsInSale.filter(
      (item) => parseFloat(item.purchasePrice || 0) === 0
    );

    if (productsWithZeroPurchasePrice.length > 0) {
      message.error(
        "Purchase price for the selected products must be greater than 0."
      );
      return;
    }

    if (values.status === "completed" && !paymentMethod) {
      message.error("Payment method is required for completed sales.");
      return;
    }

    setLoading(true);
    try {
      const { totalAmount, totalAmountWithoutTax } = calculateSummary();

      const fullTotalAmount = parseFloat(totalAmount) + totalAdditionalCharges;

      const saleData = {
        ...values,
        storeId: form.getFieldValue("storeId"),
        sallerId: user?.id,
        paidAmount: parseFloat(paidAmount),
        balanceAmount: (fullTotalAmount - parseFloat(paidAmount)).toFixed(2),
        paymentMethod,
        deliveryShippingCharge: additionalCharges.delivery,
        servicePackagingCharge: additionalCharges.service,
        otherCharge: additionalCharges.other,
        finance: financeMethod,
        cash: parseFloat(cashAmount),
        online: parseFloat(onlineAmount),
        rcInsuranceStatus: pendingInsuranceApproval ? "pending" : "",
        products: productsInSale.map((item) => ({
          product: {
            id: item.product.id,
            name: item.product.name,
          },
          hsn_sac: item.hsn_sac,
          mrp: item.mrp,
          quantity: item.quantity,
          discount: item.discount,
          salePrice: item.salePrice,
          purchasePrice: item.purchasePrice,

          batchNumber: item.batchNumber || null,
          tracking_number: item.trackingNumber || null,

          taxes: item.taxes
            ? {
                taxId: item.taxes.id || item.taxes._id,
                taxName:
                  taxes.find((t) => t._id === (item.taxes.id || item.taxes._id))
                    ?.taxName || "",
                percentage: item.taxes.percentage || 0,
              }
            : null,
        })),
        discount: saleDiscount,
        totalAmountWithoutTax,
        totalAmount: fullTotalAmount.toFixed(2),
        productFinanceDetails:
          productFinanceDetails.length > 0 ? productFinanceDetails : [],
      };

      setSaleFormData(saleData);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error("Error preparing sale data:", error);
      message.error("Failed to prepare sale data.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSale = async () => {
    if (!saleFormData) return;

    setLoading(true);
    try {
      const res = await API.post("/sales", saleFormData);
      if (res.status === 200) {
        message.success("Sale created successfully!");
        setSaleId(res?.data?.sale?._id);
        setShowConfirmationModal(false);
        setSaleFormData(null);
        setShowInvoiceModel(true);
      }
    } catch (error) {
      console.error("Error saving sale:", error);
      message.error(error?.response?.data?.message || "Failed to save sale.");
    } finally {
      setLoading(false);
    }
  };

  const addFinanceDetail = () => {
    setProductFinanceDetails((prev) => [...prev, { key: "", value: "" }]);
  };

  const updateFinanceDetail = (index, field, value) => {
    setProductFinanceDetails((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const removeFinanceDetail = (index) => {
    setProductFinanceDetails((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (user && stores.length > 0) {
      const store = stores.find((store) => store?._id === user?.storeId);
      if (store) {
        form.setFieldsValue({ storeId: store?._id });
        setSelectedStoreId(store?._id);
      } else if (stores.length === 1) {
        form.setFieldsValue({ storeId: stores[0]?._id });
        setSelectedStoreId(stores[0]?._id);
      }
    }
  }, [user, stores, form]);

  const calculateTotalAmount = (products) =>
    products.reduce(
      (total, product) => total + calculateProductTotal(product),
      0
    );

  const totalAmountMemo = useMemo(
    () => calculateTotalAmount(productsInSale),
    [productsInSale]
  );

  useEffect(() => {
    form.setFieldsValue({ totalAmount: totalAmountMemo });
    const { totalAmount } = calculateSummary();

    const fullTotal = parseFloat(totalAmount) + totalAdditionalCharges;


    if (!isPaidAmountEdited) {
      setPaidAmount(fullTotal.toFixed(2));
    }
    setBalanceAmount((fullTotal - parseFloat(paidAmount || 0)).toFixed(2));
  }, [
    totalAmountMemo,
    saleDiscount,
    isPaidAmountEdited,
    paidAmount,
    form,
    additionalCharges,
  ]);

  const handleAddCustomer = async (values) => {
    try {
      const { address, ...rest } = values;
      const payload = {
        ...rest,
        address: {
          street: address?.street,
          city: address?.city,
          state: address?.state,
          pincode: address?.pincode,
        },
      };
      const res = await API.post("/customers", payload);
      if (res?.status === 200) message.success("Customer added successfully!");
      formCustomer.resetFields();
      setShowCustomerModel(false);
      fetchData();
    } catch (error) {
      console.error("Submit failed:", error);
      message.error(error.response?.data?.message || "Submit failed.");
    }
  };

  const handleAddProduct = () => {
    if (!selectedProduct || !selectedStoreId) {
      message.error("Please select a product and a store.");
      return;
    }

    // Enforce tracking number selection if enabled
    if (storeSettings.trackingNumber === "Yes") {
      if (trackingNumbers.length === 0) {
        message.error(
          "No tracking numbers available. Please add stock or tracking number first before selling."
        );
        return;
      }

      if (!trackingNo) {
        message.error("Please select a tracking number to proceed.");
        return;
      }
    }

    const selectedBatch =
      selectedProduct.batches.find(
        (batch) =>
          batch.tracking_number === trackingNo &&
          batch.batchNumber === batchNumber
      ) ||
      (selectedProduct.batches.length > 0 ? selectedProduct.batches[0] : null);

    const productQuantity = selectedBatch
      ? selectedBatch.quantity
      : selectedProduct.quantity || 0;

    if (productQuantity <= 0) {
      message.warning(
        `Insufficient stock for ${selectedProduct.product.name}. Only ${productQuantity} items available.`
      );
      return;
    }

    const existingProductIndex = productsInSale.findIndex(
      (item) =>
        item.product.id === selectedProduct.product.id &&
        item.trackingNumber ===
          (trackingNo ||
            (selectedBatch ? selectedBatch.tracking_number || "" : "")) &&
        item.batchNumber ===
          (batchNumber ||
            (selectedBatch ? selectedBatch.batchNumber || "" : ""))
    );

    if (existingProductIndex !== -1) {
      // Product with same tracking number and batch number exists, increment quantity
      const existingProduct = productsInSale[existingProductIndex];
      const newQuantity = existingProduct.quantity + 1;

      if (newQuantity > productQuantity) {
        message.warning(
          `Insufficient stock for ${selectedProduct.product.name}. Only ${productQuantity} items available.`
        );
        return;
      }

      setProductsInSale((prev) =>
        prev.map((item, index) =>
          index === existingProductIndex
            ? {
                ...item,
                quantity: newQuantity,
                totalPrice: calculateProductTotal({
                  ...item,
                  quantity: newQuantity,
                }),
              }
            : item
        )
      );
    } else {
      // Add new product
      const newProduct = {
        product: {
          id: selectedProduct.product.id,
          name: selectedProduct.product.name,
        },
        batchId: selectedBatch ? selectedBatch._id : null,
        batchNumber:
          batchNumber || (selectedBatch ? selectedBatch.batchNumber || "" : ""),
        trackingNumber:
          trackingNo ||
          (selectedBatch ? selectedBatch.tracking_number || "" : ""),
        quantity: 1,
        code: selectedProduct.barcode || null,
        hsn_sac: selectedProduct.hsn_sac || "",
        mrp: selectedBatch ? selectedBatch.mrp || 0 : selectedProduct.mrp || 0,
        salePrice: selectedBatch
          ? selectedBatch.sellingPrice || 0
          : selectedProduct.salePrice || 0,
        purchasePrice: selectedBatch
          ? selectedBatch.purchasePrice || 0
          : selectedProduct.purchasePrice || 0,
        taxes: selectedBatch
          ? {
              id: selectedBatch.gst?.id || "",
              percentage: selectedBatch.gst?.percentage || 0,
            }
          : selectedProduct.taxes || null,
        discount: { value: 0, type: "percentage" },
        totalPrice: calculateProductTotal({
          salePrice: selectedBatch
            ? selectedBatch.sellingPrice || 0
            : selectedProduct.salePrice || 0,
          purchasePrice: selectedBatch
            ? selectedBatch.purchasePrice || 0
            : selectedProduct.purchasePrice || 0,
          quantity: 1,
          discount: { value: 0, type: "percentage" },
          taxes: selectedBatch
            ? {
                id: selectedBatch.gst?.id || "",
                percentage: selectedBatch.gst?.percentage || 0,
              }
            : selectedProduct.taxes || null,
        }),
      };

      setProductsInSale((prev) => [...prev, newProduct]);
      setProducts((prev) =>
        prev.filter((p) => p.product.id !== selectedProduct.product.id)
      );
    }

    setSelectedProduct(null);
    setTrackingNo(null);
    setBatchNumber(null);
    form.setFieldsValue({ trackingNo: undefined, batchNumber: undefined });
  };

  const handleRemoveProduct = (productId, trackingNumber, batchNumber) => {
    setProductsInSale((prev) =>
      prev.filter(
        (item) =>
          !(
            item.product.id === productId &&
            item.trackingNumber === trackingNumber &&
            item.batchNumber === batchNumber
          )
      )
    );
  };

  const handleQuantityChange = (
    productId,
    trackingNumber,
    batchNumber,
    quantity
  ) => {
    if (quantity < 1) return;

    const product = productsInSale.find(
      (item) =>
        item.product.id === productId &&
        item.trackingNumber === trackingNumber &&
        item.batchNumber === batchNumber
    );
    if (!product) return;

    const stock = Array.isArray(allStocks)
      ? allStocks.find(
          (s) =>
            s.storeId?._id === selectedStoreId && s.productId?._id === productId
        )
      : null;

    const batchQuantity =
      stock?.batch?.find((b) => b._id === product.batchId)?.quantity ||
      stock?.quantity ||
      0;

    if (quantity > batchQuantity) {
      message.warning(
        `Insufficient stock for ${product.product.name}. Only ${batchQuantity} items available.`
      );
      return;
    }

    setProductsInSale((prev) =>
      prev.map((item) => {
        if (
          item.product.id === productId &&
          item.trackingNumber === trackingNumber &&
          item.batchNumber === batchNumber
        ) {
          return {
            ...item,
            quantity,
            totalPrice: calculateProductTotal({ ...item, quantity }),
          };
        }
        return item;
      })
    );
  };

  const handleSalePriceChange = (
    productId,
    trackingNumber,
    batchNumber,
    newSalePrice
  ) => {
    setProductsInSale((prev) =>
      prev.map((item) => {
        if (
          item.product.id === productId &&
          item.trackingNumber === trackingNumber &&
          item.batchNumber === batchNumber
        ) {
          return {
            ...item,
            salePrice: parseFloat(newSalePrice) || 0,
            totalPrice: calculateProductTotal({
              ...item,
              salePrice: parseFloat(newSalePrice) || 0,
            }),
          };
        }
        return item;
      })
    );
  };

  const handlePurchasePriceChange = (
    productId,
    trackingNumber,
    batchNumber,
    newPurchasePrice
  ) => {
    setProductsInSale((prev) =>
      prev.map((item) => {
        if (
          item.product.id === productId &&
          item.trackingNumber === trackingNumber &&
          item.batchNumber === batchNumber
        ) {
          return {
            ...item,
            purchasePrice: parseFloat(newPurchasePrice) || 0,
            totalPrice: calculateProductTotal({ ...item }),
          };
        }
        return item;
      })
    );
  };

  const handleDiscountChange = (
    productId,
    trackingNumber,
    batchNumber,
    discountType,
    discountValue
  ) => {
    setProductsInSale((prev) =>
      prev.map((item) => {
        if (
          item.product.id === productId &&
          item.trackingNumber === trackingNumber &&
          item.batchNumber === batchNumber
        ) {
          const baseTotal = item.salePrice * item.quantity;
          if (discountType === "percentage" && discountValue > 100) {
            message.error("Percentage discount cannot exceed 100%.");
            return item;
          }
          if (discountType === "flat" && discountValue > baseTotal) {
            message.error("Flat discount cannot exceed the total price.");
            return item;
          }
          return {
            ...item,
            discount: {
              type: discountType,
              value: parseFloat(discountValue) || 0,
            },
            totalPrice: calculateProductTotal({
              ...item,
              discount: {
                type: discountType,
                value: parseFloat(discountValue) || 0,
              },
            }),
          };
        }
        return item;
      })
    );
  };

  const calculateSummary = () => {
    let netTotal = 0; // total with GST
    let totalProductDiscount = 0;
    let totalTaxableAmount = 0; // total GST amount
    let totalBaseAmount = 0; // total without GST

    // Step 1: Calculate product-level totals and store product details
    const productDetails = productsInSale.map((item) => {
      const totalWithTax = item.salePrice * item.quantity; // salePrice includes GST
      const taxRate = item.taxes?.percentage || 0;

      // Reverse GST calculation
      const baseTotal = totalWithTax / (1 + taxRate / 100);
      const taxAmount = totalWithTax - baseTotal;

      // Product-level discount (on base price)
      const discount =
        item.discount?.type === "percentage"
          ? (item.discount.value / 100) * baseTotal
          : (item.discount?.value || 0) * item.quantity;

      const baseAfterDiscount = baseTotal - discount;
      const taxAfterDiscount = (baseAfterDiscount * taxRate) / 100;

      netTotal += totalWithTax; // with GST
      totalProductDiscount += discount;
      totalTaxableAmount += taxAfterDiscount;
      totalBaseAmount += baseAfterDiscount;

      return {
        baseAfterDiscount,
        taxRate,
      };
    });

    // Step 2: Calculate sale-level discount
    let saleLevelDiscount = 0;
    if (saleDiscount.value > 0) {
      if (saleDiscount.type === "percentage") {
        saleLevelDiscount = (saleDiscount.value / 100) * totalBaseAmount;
      } else if (saleDiscount.type === "flat") {
        saleLevelDiscount = Math.min(saleDiscount.value, totalBaseAmount);
      }
    }

    const totalBaseAfterSaleDiscount = Math.max(
      totalBaseAmount - saleLevelDiscount,
      0
    );

    // Step 3: Apply sale-level discount proportionally and recalculate tax for each product
    let totalTaxAfterSaleDiscount = 0;
    
    if (totalBaseAmount > 0) {
      productDetails.forEach((product) => {
        // Calculate this product's proportion of the total base
        const productProportion = product.baseAfterDiscount / totalBaseAmount;
        
        // Apply sale-level discount proportionally to this product
        const productBaseAfterSaleDiscount = totalBaseAfterSaleDiscount * productProportion;
        
        // Calculate tax on this product's amount after sale discount
        const productTax = (productBaseAfterSaleDiscount * product.taxRate) / 100;
        
        totalTaxAfterSaleDiscount += productTax;
      });
    }

    const totalAmount = totalBaseAfterSaleDiscount + totalTaxAfterSaleDiscount;

    return {
      netTotal: netTotal.toFixed(2), // with GST
      totalDiscount: (totalProductDiscount + saleLevelDiscount).toFixed(2),
      taxableAmount: totalTaxAfterSaleDiscount.toFixed(2), // total GST
      totalAmountWithoutTax: totalBaseAfterSaleDiscount.toFixed(2), // base amount
      totalAmount: totalAmount.toFixed(2), // final amount
    };
  };

  const handlePaidAmountChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "" || /^\d*\.?\d{0,2}$/.test(inputValue)) {
      setPaidAmount(inputValue);
      setIsPaidAmountEdited(true);
      const { totalAmount } = calculateSummary();
      const fullTotal = parseFloat(totalAmount) + totalAdditionalCharges;
      const newPaidAmount = inputValue === "" ? 0 : parseFloat(inputValue);
      setBalanceAmount((fullTotal - newPaidAmount).toFixed(2));
    } else {
      message.warning("Only 2 decimal places are allowed.");
    }
  };

  const handlePaidAmountBlur = () => {
    const parsedValue =
      paidAmount === "" ? "0.00" : parseFloat(paidAmount || 0).toFixed(2);
    setPaidAmount(parsedValue);
    const { totalAmount } = calculateSummary();
    const fullTotal = parseFloat(totalAmount) + totalAdditionalCharges;
    setBalanceAmount((fullTotal - parseFloat(parsedValue)).toFixed(2));
  };

  const formatNumber = (number) => `₹${new Intl.NumberFormat().format(number)}`;

  const columns = [
    {
      title: "Product",
      dataIndex: ["product", "name"],
      key: "productName",
    },
    ...(storeSettings.trackingNumber === "Yes"
      ? [
          {
            title: "Tracking No",
            dataIndex: "trackingNumber",
            key: "trackingNumber",
            render: (trackingNumber) => trackingNumber || "N/A",
          },
        ]
      : []),
    ...(storeSettings.batchNumber === "Yes"
      ? [
          {
            title: "Batch No",
            dataIndex: "batchNumber",
            key: "batchNumber",
            render: (batchNumber) => batchNumber || "N/A",
          },
        ]
      : []),
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <InputNumber
          min={1}
          value={record.quantity}
          onChange={(value) =>
            handleQuantityChange(
              record.product.id,
              record.trackingNumber,
              record.batchNumber,
              Number(value)
            )
          }
          precision={0}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Hsn/Sac",
      dataIndex: "hsn_sac",
      key: "hsn_sac",
      render: (hsn_sac) => hsn_sac || "N/A",
    },
    {
      title: "Dis.",
      key: "discount",
      render: (_, record) => (
        <Space>
          <InputNumber
            addonAfter={
              <Select
                value={record.discount?.type || "percentage"}
                onChange={(value) =>
                  handleDiscountChange(
                    record.product.id,
                    record.trackingNumber,
                    record.batchNumber,
                    value,
                    record.discount?.value || 0
                  )
                }
              >
                <Option value="percentage">%</Option>
                <Option value="flat">Flat</Option>
              </Select>
            }
            min={0}
            placeholder="Discount"
            value={record.discount?.value || ""}
            onChange={(value) => {
              if (value < 0) {
                message.error("Negative values are not allowed.");
                return;
              }
              handleDiscountChange(
                record.product.id,
                record.trackingNumber,
                record.batchNumber,
                record.discount?.type || "percentage",
                value
              );
            }}
            precision={2}
            style={{ width: "150px" }}
          />
        </Space>
      ),
    },
    {
      title: "S. Price",
      dataIndex: "salePrice",
      key: "salePrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          value={record.salePrice}
          onChange={(value) =>
            handleSalePriceChange(
              record.product.id,
              record.trackingNumber,
              record.batchNumber,
              value
            )
          }
          precision={2}
          style={{
            width: "100%",
            borderColor:
              parseFloat(record.purchasePrice || 0) >
              parseFloat(record.salePrice || 0)
                ? "#ff4d4f"
                : "#d9d9d9",
          }}
        />
      ),
    },
    {
      title: "P. Price",
      dataIndex: "purchasePrice",
      key: "purchasePrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          value={record.purchasePrice}
          onChange={(value) =>
            handlePurchasePriceChange(
              record.product.id,
              record.trackingNumber,
              record.batchNumber,
              value
            )
          }
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Tax",
      key: "taxes",
      render: (_, record) => (
        <Select
          style={{ minWidth: 100 }}
          placeholder="Select Tax"
          value={record.taxes?.id || null}
          onChange={(value) => {
            const selectedTax = taxes.find((tax) => tax._id === value);
            setProductsInSale((prev) =>
              prev.map((item) =>
                item.product.id === record.product.id &&
                item.trackingNumber === record.trackingNumber &&
                item.batchNumber === record.batchNumber
                  ? {
                      ...item,
                      taxes: selectedTax
                        ? {
                            id: selectedTax._id,
                            percentage: selectedTax.percentage,
                          }
                        : null,
                      totalPrice: calculateProductTotal({
                        ...item,
                        taxes: selectedTax
                          ? {
                              id: selectedTax._id,
                              percentage: selectedTax.percentage,
                            }
                          : null,
                      }),
                    }
                  : item
              )
            );
          }}
          status={!record.taxes ? "error" : ""}
        >
          {taxes.length === 0 ? (
            <Option disabled>No Tax available</Option>
          ) : (
            taxes.map((tax) => (
              <Option key={tax._id} value={tax._id}>
                {`${tax.taxName} (${tax.percentage}%)`}
              </Option>
            ))
          )}
        </Select>
      ),
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (value) => formatNumber(value),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() =>
            handleRemoveProduct(
              record.product.id,
              record.trackingNumber,
              record.batchNumber
            )
          }
        />
      ),
    },
  ];

  const totalAdditionalCharges = Object.values(additionalCharges)
    .filter((val) => val && !isNaN(val))
    .reduce((sum, val) => sum + Number(val), 0);

  return (
    <div
      style={{
        padding: "10px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6} md={6}>
            <Form.Item
              name="storeId"
              label="Select Store"
              style={{ marginTop: 2 }}
            >
              <Select
                placeholder={
                  stores.length === 1 ? "Store (Auto-selected)" : "Select Store"
                }
                disabled={user?.role !== "admin" || stores.length === 1}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.props?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(value) => setSelectedStoreId(value)}
              >
                {stores.length === 0 ? (
                  <Option disabled>No stores available</Option>
                ) : (
                  stores.map((store) => (
                    <Option key={store?._id} value={store?._id}>
                      {store?.name}
                    </Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6} md={7}>
            <Form.Item
              name="customerId"
              label={
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Customer</span>
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined />}
                    size="small"
                    style={{ marginLeft: 20 }}
                    onClick={() => setShowCustomerModel(true)}
                  />
                </div>
              }
              rules={[{ required: true, message: "Please select a customer." }]}
              style={{ width: "100%", marginBottom: 0 }}
            >
              <Select
                placeholder="Select Customer"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.props?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {customers.map((customer) => (
                  <Option key={customer?._id} value={customer?._id}>
                    {customer?.name
                      ? `${customer.name} (${customer.phone})`
                      : customer?.phone}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={6} md={8}>
            <Form.Item
              name="employeeId"
              label="Employee (Sold By)"
            >
              <Select
                placeholder="Select Employee"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.props?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {employees.map((employee) => (
                  <Option key={employee?._id} value={employee?._id}>
                    {employee?.name
                      ? `${employee.name} (${employee.phone})`
                      : employee?.phone}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={6} md={3}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true }]}
              initialValue={"completed"}
            >
              <Select placeholder="Select Status">
                <Option value="pending">Pending</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
          </Col>
          
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: "10px" }}>
          <Col
            xs={24}
            sm={
              storeSettings.trackingNumber === "Yes" ||
              storeSettings.batchNumber === "Yes"
                ? 10
                : 18
            }
          >
            <Select
              placeholder="Select Product"
              value={selectedProduct ? selectedProduct.product.id : undefined}
              onChange={(value) => {
                const product = products.find(
                  (prod) => prod.product.id === value
                );
                setSelectedProduct(product || null);
                setTrackingNo(null);
                setBatchNumber(null);
                form.setFieldsValue({
                  trackingNo: undefined,
                  batchNumber: undefined,
                });
              }}
              onSearch={(input) => {
                if (input.trim() === "") {
                  setProducts([]);
                  debouncedFetch(
                    input,
                    excludedProductIds,
                    selectedStoreId,
                    currentPage,
                    pageLimit
                  );
                  return;
                }
                debouncedFetch(
                  input,
                  excludedProductIds,
                  selectedStoreId,
                  currentPage,
                  pageLimit
                );
              }}
              style={{ flex: 1, width: "100%" }}
              showSearch
              optionLabelProp="label"
              filterOption={(input, option) => {
                const searchText = input.toLowerCase().trim();
                const product = products.find(
                  (p) => p.product.id === option.key
                );
                if (!product) return false;

                const searchableText = `
      ${product.product.name || ""}
      ${product.barcode || ""}
      ${product.trackingNumber || ""}
      ${product.batchNumber || ""}
      ${product.hsn_sac || ""}
    `
                  .toLowerCase()
                  .replace(/\s+/g, " ")
                  .trim();

                return searchableText.includes(searchText);
              }}
              filterSort={(optionA, optionB) => {
                const textA = optionA.optionText.toLowerCase();
                const textB = optionB.optionText.toLowerCase();
                return textA.localeCompare(textB);
              }}
              notFoundContent={
                isLoading ? (
                  <div style={{ textAlign: "center", padding: "10px" }}>
                    <Spin size="small" /> Loading products...
                  </div>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No products found"
                  />
                )
              }
            >
              {products.map((product) => {
                const optionText = [product.product.name || "Unnamed Product"]
                  .filter(Boolean)
                  .join(" ")
                  .trim();
                return (
                  <Option
                    key={product.product.id}
                    value={product.product.id}
                    label={optionText}
                    optionText={optionText}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{optionText}</span>
                      <span>
                        <span>{`${formatNumber(product.salePrice)}`}</span>{" "}
                        <span>{`(In Stock: ${product.quantity || 0})`}</span>
                      </span>
                    </div>
                  </Option>
                );
              })}
            </Select>
          </Col>
          {storeSettings.trackingNumber === "Yes" ? (
            <Col xs={24} sm={4}>
              <Form.Item name="trackingNo">
                <Select
                  placeholder="Select Tracking Number"
                  value={trackingNo}
                  onChange={(value) => setTrackingNo(value)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={
                    trackingNumbers.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No tracking numbers available"
                      />
                    ) : null
                  }
                >
                  {trackingNumbers.map((tracking) => (
                    <Option key={tracking} value={tracking}>
                      {tracking}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          ) : null}
          {storeSettings.batchNumber === "Yes" ? (
            <Col xs={24} sm={4}>
              <Form.Item name="batchNumber">
                <Select
                  placeholder="Select Batch Number"
                  value={batchNumber}
                  onChange={(value) => setBatchNumber(value)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={
                    batchNumbers.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No batch numbers available"
                      />
                    ) : null
                  }
                >
                  {batchNumbers.map((batch) => (
                    <Option key={batch} value={batch}>
                      {batch}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          ) : null}
          <Col xs={24} sm={4} style={{ display: "flex", gap: "10px" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
              disabled={!selectedProduct || !selectedStoreId}
              style={{ flex: 1, whiteSpace: "nowrap", maxWidth: 200 }}
            >
              Add Product
            </Button>
          </Col>
        </Row>

        <div style={{ overflowX: "auto", marginBottom: 16 }}>
          <Table
            columns={columns}
            dataSource={productsInSale}
            rowKey={(record) =>
              `${record.product.id}-${record.trackingNumber || "no-tracking"}-${
                record.batchNumber || "no-batch"
              }`
            }
            pagination={false}
            scroll={{ x: 1000 }}
            footer={() => {
              const {
                taxableAmount,
                netTotal,
                totalDiscount,
                totalAmount,
                totalAmountWithoutTax,
              } = calculateSummary();

              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    marginTop: "16px",
                    borderTop: "2px solid #e8e8e8",
                    padding: "16px 0",
                    backgroundColor: "#fafafa",
                    borderRadius: "8px",
                  }}
                >
                  <Row gutter={[24, 16]} align="top">
                    <Col xs={24} lg={16}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "16px",
                          padding: "20px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <div>
                          <h4
                            style={{
                              margin: "0 0 12px 0",
                              color: "#1890ff",
                              fontSize: "16px",
                              fontWeight: "600",
                              borderBottom: "1px solid #e8e8e8",
                              paddingBottom: "8px",
                            }}
                          >
                            Sale Discount & Payment
                          </h4>
                          <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={12} md={8}>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  color: "#595959",
                                  marginBottom: "8px",
                                }}
                              >
                                Sale Discount
                              </label>
                              <InputNumber
                                addonAfter={
                                  <Select
                                    value={saleDiscount.type}
                                    onChange={(value) =>
                                      setSaleDiscount((prev) => ({
                                        ...prev,
                                        type: value,
                                      }))
                                    }
                                    style={{ width: 80 }}
                                  >
                                    <Option value="percentage">%</Option>
                                    <Option value="flat">Flat</Option>
                                  </Select>
                                }
                                min={0}
                                placeholder="Enter discount"
                                value={saleDiscount.value || ""}
                                onChange={(value) => {
                                  if (value < 0) {
                                    message.error(
                                      "Negative values are not allowed."
                                    );
                                    return;
                                  }
                                  if (
                                    saleDiscount.type === "percentage" &&
                                    value > 100
                                  ) {
                                    message.error(
                                      "Percentage discount cannot exceed 100%."
                                    );
                                    return;
                                  }
                                  if (
                                    saleDiscount.type === "flat" &&
                                    value > Number(netTotal)
                                  ) {
                                    message.error(
                                      "Flat discount cannot exceed the total amount."
                                    );
                                    return;
                                  }
                                  setSaleDiscount((prev) => ({
                                    ...prev,
                                    value,
                                  }));
                                }}
                                precision={2}
                                style={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  borderColor: "#d9d9d9",
                                }}
                              />
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  color: "#595959",
                                  marginBottom: "8px",
                                }}
                              >
                                Paid Amount
                              </label>
                              <Input
                                placeholder="Enter paid amount"
                                value={paidAmount}
                                onChange={handlePaidAmountChange}
                                onBlur={handlePaidAmountBlur}
                                style={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  borderColor: "#d9d9d9",
                                }}
                              />
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  color: "#595959",
                                  marginBottom: "8px",
                                }}
                              >
                                Balance Amount
                              </label>
                              <Input
                                placeholder="Balance"
                                value={balanceAmount}
                                readOnly
                                style={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  borderColor: "#d9d9d9",
                                  backgroundColor: "#f5f5f5",
                                  color: "#8c8c8c",
                                }}
                              />
                            </Col>
                          </Row>

                          <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                            <Col xs={24}>
                              <Button
                                icon={<PlusOutlined />}
                                onClick={() => setShowAdditional((prev) => !prev)}
                                style={{
                                  padding: "8px 10px",
                                  backgroundColor: "#1890ff",
                                  color: "#fff",
                                  fontWeight: 600,
                                  borderRadius: "8px",
                                  border: "none",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                  cursor: "pointer",
                                  transition: "background 0.15s, color 0.15s",
                                  outline: "none",
                                  marginBottom: "12px",
                                }}
                              >
                                Additional Charges
                              </Button>

                              {showAdditional && (
                                <div
                                  style={{
                                    background: "#fff",
                                    borderRadius: "10px",
                                    padding: "14px 18px",
                                    border: "1px solid #f0f0f0",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <Row gutter={[8, 0]}>

                                  <Col xs={24} sm={8}>
                                      <label
                                        style={{
                                          display: "block",
                                          fontWeight: 500,
                                          fontSize: "13px",
                                          color: "#595959",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Service / Maker Charge
                                      </label>
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                        value={additionalCharges.service}
                                        onChange={(value) =>
                                          setAdditionalCharges((charges) => ({
                                            ...charges,
                                            service: value,
                                          }))
                                        }
                                        placeholder="Service charge"
                                      />
                                    </Col>
                                    
                                    <Col xs={24} sm={8}>
                                      <label
                                        style={{
                                          display: "block",
                                          fontWeight: 500,
                                          fontSize: "13px",
                                          color: "#595959",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Delivery / Shipping Charge
                                      </label>
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                        value={additionalCharges.delivery}
                                        onChange={(value) =>
                                          setAdditionalCharges((charges) => ({
                                            ...charges,
                                            delivery: value,
                                          }))
                                        }
                                        placeholder="Delivery charge"
                                      />
                                    </Col>

                                   

                                    <Col xs={24} sm={8}>
                                      <label
                                        style={{
                                          display: "block",
                                          fontWeight: 500,
                                          fontSize: "13px",
                                          color: "#595959",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Other Charges
                                      </label>
                                      <InputNumber
                                        min={0}
                                        style={{ width: "100%" }}
                                        value={additionalCharges.other}
                                        onChange={(value) =>
                                          setAdditionalCharges((charges) => ({
                                            ...charges,
                                            other: value,
                                          }))
                                        }
                                        placeholder="Other charge"
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              )}
                            </Col>
                          </Row>

                          <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                            <Col xs={24} sm={12} md={8}>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  color: "#595959",
                                  marginBottom: "8px",
                                }}
                              >
                                Payment Method{" "}
                                <span
                                  className="error"
                                  style={{ color: "#ff4d4f" }}
                                >
                                  *
                                </span>
                              </label>
                              <Select
                                placeholder="Choose payment method"
                                value={paymentMethod}
                                onChange={(value) => setPaymentMethod(value)}
                                style={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  borderColor:
                                    form.getFieldValue("status") ===
                                      "completed" && !paymentMethod
                                      ? "#ff4d4f"
                                      : "#d9d9d9",
                                }}
                                status={
                                  form.getFieldValue("status") ===
                                    "completed" && !paymentMethod
                                    ? "error"
                                    : ""
                                }
                                options={[
                                  { value: "cash", label: "Cash" },
                                  { value: "upi", label: "UPI" },
                                  {
                                    value: "credit_card",
                                    label: "Credit Card",
                                  },
                                  { value: "debit_card", label: "Debit Card" },
                                  {
                                    value: "bank_transfer",
                                    label: "Bank Transfer",
                                  },
                                  {
                                    value: "cash_online",
                                    label: "Cash And Online",
                                  },
                                  { value: "other", label: "Other" },
                                ]}
                              />
                            </Col>
                            {storeSettings.trackingNumber === "Yes" ? (
                              <Col xs={24} sm={12} md={8}>
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#595959",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Pending Insurance Approval
                                </label>
                                <Checkbox
                                  checked={pendingInsuranceApproval}
                                  onChange={(e) =>
                                    setPendingInsuranceApproval(
                                      e.target.checked
                                    )
                                  }
                                  style={{ width: "100%", borderRadius: "6px" }}
                                >
                                  Mark as Pending
                                </Checkbox>
                              </Col>
                            ) : (
                              ""
                            )}

                            <Col xs={24} sm={12} md={16}>
                              {paymentMethod === "cash_online" && (
                                <div>
                                  <Row gutter={[8, 8]} align="middle">
                                    <Col xs={12}>
                                      <label
                                        style={{
                                          display: "block",
                                          fontSize: "14px",
                                          fontWeight: "500",
                                          color: "#595959",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Cash Amount
                                      </label>
                                      <Input
                                        type="number"
                                        placeholder="Cash amount"
                                        value={cashAmount}
                                        onChange={(e) =>
                                          setCashAmount(e.target.value || 0)
                                        }
                                        style={{
                                          width: "100%",
                                          borderRadius: "6px",
                                          borderColor: "#d9d9d9",
                                        }}
                                      />
                                    </Col>
                                    <Col xs={12}>
                                      <label
                                        style={{
                                          display: "block",
                                          fontSize: "14px",
                                          fontWeight: "500",
                                          color: "#595959",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Bank/UPI Amount
                                      </label>
                                      <Input
                                        type="number"
                                        placeholder="Online amount"
                                        value={onlineAmount}
                                        onChange={(e) =>
                                          setOnlineAmount(e.target.value || 0)
                                        }
                                        style={{
                                          width: "100%",
                                          borderRadius: "6px",
                                          borderColor: "#d9d9d9",
                                        }}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              )}
                            </Col>
                          </Row>
                        </div>

                        <div>
                          <h4
                            style={{
                              margin: "0 0 12px 0",
                              color: "#1890ff",
                              fontSize: "16px",
                              fontWeight: "600",
                              borderBottom: "1px solid #e8e8e8",
                              paddingBottom: "8px",
                            }}
                          >
                            Finance & Warranty Details
                          </h4>
                          <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={12} md={8}>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  color: "#595959",
                                  marginBottom: "8px",
                                }}
                              >
                                Select Financier
                              </label>
                              <Select
                                placeholder="Choose financier"
                                value={financeMethod || null}
                                onChange={(value) => setFinanceMethod(value)}
                                allowClear
                                style={{ width: "100%", borderRadius: "6px" }}
                                options={[
                                  { value: "tvsFinance", label: "TVS FINANCE" },
                                  {
                                    value: "bajajFinanceLtd",
                                    label: "BAJAJ FINANCE LTD",
                                  },
                                  {
                                    value: "axisBankLtd",
                                    label: "AXIS BANK LTD",
                                  },
                                  {
                                    value: "hdbFinancialServicesLtd",
                                    label: "HDB FINANCIAL SERVICES LTD",
                                  },
                                  {
                                    value: "idfcFirstBankLtd",
                                    label: "IDFC FIRST BANK LTD",
                                  },
                                  {
                                    value: "tvsCreditServicesLtd",
                                    label: "TVS CREDIT SERVICES LTD",
                                  },
                                ]}
                              />
                            </Col>

                            <Col xs={24} sm={12} md={24}>
                              <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={addFinanceDetail}
                                style={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  marginBottom: "12px",
                                  height: "40px",
                                }}
                              >
                                Add Warranty Detail
                              </Button>
                            </Col>
                          </Row>

                          {productFinanceDetails.map((detail, index) => (
                            <Row
                              gutter={[8, 8]}
                              key={index}
                              style={{ marginBottom: "12px" }}
                            >
                              <Col xs={11}>
                                <Input
                                  placeholder="Sl/IMEI"
                                  value={detail.key}
                                  onChange={(e) =>
                                    updateFinanceDetail(
                                      index,
                                      "key",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    borderRadius: "6px",
                                    borderColor: "#d9d9d9",
                                  }}
                                />
                              </Col>
                              <Col xs={11}>
                                <Input
                                  placeholder="xxxxxxxxxx"
                                  value={detail.value}
                                  onChange={(e) =>
                                    updateFinanceDetail(
                                      index,
                                      "value",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    borderRadius: "6px",
                                    borderColor: "#d9d9d9",
                                  }}
                                />
                              </Col>
                              <Col xs={2}>
                                <Button
                                  danger
                                  onClick={() => removeFinanceDetail(index)}
                                  style={{
                                    borderRadius: "6px",
                                    width: "100%",
                                  }}
                                >
                                  X
                                </Button>
                              </Col>
                            </Row>
                          ))}
                        </div>
                      </div>
                    </Col>

                    <Col xs={24} lg={8}>
                      <div
                        style={{
                          padding: "20px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                          height: "fit-content",
                          position: "sticky",
                          top: "20px",
                        }}
                      >
                        <h4
                          style={{
                            margin: "0 0 16px 0",
                            color: "#1890ff",
                            fontSize: "18px",
                            fontWeight: "600",
                            textAlign: "center",
                            borderBottom: "2px solid #e8e8e8",
                            paddingBottom: "12px",
                          }}
                        >
                          Bill Details
                        </h4>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#595959",
                                fontWeight: "500",
                              }}
                            >
                              Net Total
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                color: "#ff4d4f",
                                fontWeight: "600",
                              }}
                            >
                              {formatNumber(netTotal)}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#595959",
                                fontWeight: "500",
                              }}
                            >
                              Discount
                            </span>
                            <span
                              style={{ fontSize: "16px", color: "#595959" }}
                            >
                              {formatNumber(totalDiscount)}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#595959",
                                fontWeight: "500",
                              }}
                            >
                              Tax
                            </span>
                            <span
                              style={{ fontSize: "16px", color: "#595959" }}
                            >
                              {formatNumber(taxableAmount)}
                            </span>
                          </div>

                          {additionalCharges.delivery > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "7px 0",
                                fontSize: "14px",
                                color: "#595959",
                                borderBottom: "1px solid #f1f1f1",
                                fontWeight: 500,
                                padding: "8px 0",
                              }}
                            >
                              <span>Delivery/Shipping Charge</span>
                              <span>
                                {formatNumber(additionalCharges.delivery)}
                              </span>
                            </div>
                          )}
                          {additionalCharges.service > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "7px 0",
                                fontSize: "14px",
                                color: "#595959",
                                borderBottom: "1px solid #f1f1f1",
                                fontWeight: 500,
                                padding: "8px 0",
                              }}
                            >
                              <span>Service/Packaging Charge</span>
                              <span>
                                {formatNumber(additionalCharges.service)}
                              </span>
                            </div>
                          )}
                          {additionalCharges.other > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "7px 0",
                                fontSize: "14px",
                                color: "#595959",
                                borderBottom: "1px solid #f1f1f1",
                                fontWeight: 500,
                                padding: "8px 0",
                              }}
                            >
                              <span>Others</span>
                              <span>
                                {formatNumber(additionalCharges.other)}
                              </span>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#595959",
                                fontWeight: "500",
                              }}
                            >
                              Payable (Before Tax)
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                color: "#52c41a",
                                fontWeight: "600",
                              }}
                            >
                              {formatNumber(totalAmountWithoutTax)}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "12px 0",
                              borderTop: "2px solid #1890ff",
                              marginTop: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "16px",
                                color: "#1890ff",
                                fontWeight: "600",
                              }}
                            >
                              Total Payable
                            </span>
                            <span
                              style={{
                                fontSize: "20px",
                                color: "#1890ff",
                                fontWeight: "700",
                              }}
                            >
                              {formatNumber(
                                Number(totalAmount) + totalAdditionalCharges
                              )}
                            </span>
                          </div>
                        </div>

                        <Form.Item
                          style={{ marginTop: "20px", marginBottom: 0 }}
                        >
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={isLoading}
                            block
                            disabled={!productsInSale.length}
                            icon={<SaveOutlined />}
                            style={{
                              height: "48px",
                              fontSize: "16px",
                              fontWeight: "600",
                              borderRadius: "8px",
                            }}
                          >
                            Create Sale
                          </Button>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </div>
              );
            }}
          />
        </div>
      </Form>

      {showCustomerModel && (
        <Modal
          title={
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                color: "#4a90e2",
              }}
            >
              <span style={{ fontSize: "16px" }}>ADD NEW CUSTOMER</span> <hr />
            </div>
          }
          open={showCustomerModel}
          footer={null}
          onCancel={() => setShowCustomerModel(false)}
          style={{
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
          width={800}
        >
          <Form
            form={formCustomer}
            layout="vertical"
            onFinish={handleAddCustomer}
            style={{ padding: "24px", borderRadius: "12px" }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={<Text>Customer Name</Text>}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a Customer Name!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Customer Name"
                    style={{ borderRadius: "4px", borderColor: "#d9d9d9" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<Text>Phone</Text>}
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the phone number!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Please enter the phone number"
                    style={{ borderRadius: "4px", borderColor: "#d9d9d9" }}
                  />
                </Form.Item>
              </Col>
             
            </Row>
            <Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={<Text>Vill/Street</Text>}
                    name={["address", "street"]}
                  >
                    <Input
                      placeholder="Please enter the street"
                      style={{ borderRadius: "4px", borderColor: "#d9d9d9" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={<Text>Pincode</Text>}
                    name={["address", "pincode"]}
                  >
                    <Input
                      placeholder="Enter pincode"
                      type="text"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{6}$/.test(value)) getCityState(value);
                      }}
                      style={{ borderRadius: "4px", borderColor: "#d9d9d9" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={<Text>State</Text>}
                    name={["address", "state"]}
                  >
                    <Input
                      placeholder="Please enter the state"
                      style={{ borderRadius: "4px", borderColor: "#d9d9d9" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={<Text>City</Text>}
                    name={["address", "city"]}
                  >
                    <Input
                      placeholder="Please enter the city"
                      style={{ borderRadius: "4px", borderColor: "#d9d9d9" }}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                <Form.Item
                  label="GST Number"
                  name="gstNumber"
                  rules={[
                    {
                      pattern:
                        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}[Z]{1}[A-Z0-9]{1}$/,
                      message: "Please enter a valid GST number!",
                    },
                  ]}
                >
                  <Input placeholder="GST Number" />
                </Form.Item>
              </Col>


              </Row>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                style={{ borderRadius: "4px" }}
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {showInvoiceModel && (
        <Modal
          title={
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                color: "#4a90e2",
              }}
            >
              <span style={{ fontSize: "16px" }}>
                DO YOU WANT TO PRINT INVOICE
              </span>{" "}
              <hr />
            </div>
          }
          open={showInvoiceModel}
          footer={null}
          onCancel={() => {
            setShowInvoiceModel(false);
            window.location.reload();
          }}
          style={{
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
          width={500}
        >
          <Row justify="center" style={{ marginTop: 16 }}>
            <Col>
              <Button
                type="primary"
                loading={isLoading}
                onClick={() => {
                  window.open(`/invoice-template/${saleId}`, '_blank');
                  window.location.reload();
                }}
                block
                style={{ borderRadius: "4px" }}
              >
                Yes
              </Button>
            </Col>
            <Col style={{ marginLeft: 16 }}>
              <Button
                type="primary"
                loading={isLoading}
                onClick={() => window.location.reload()}
                block
                style={{ borderRadius: "4px" }}
              >
                No
              </Button>
            </Col>
          </Row>
        </Modal>
      )}

      {showConfirmationModal && (
        <Modal
          title={
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                color: "#4a90e2",
              }}
            >
              <span style={{ fontSize: "16px" }}>CONFIRM SALE</span> <hr />
            </div>
          }
          open={showConfirmationModal}
          onCancel={() => {
            setShowConfirmationModal(false);
            setSaleFormData(null);
          }}
          footer={null}
          style={{
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
          width={500}
        >
          <div style={{ padding: "24px", textAlign: "center" }}>
            <div
              style={{
                fontSize: "16px",
                color: "#595959",
                marginBottom: "24px",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to create this sale invoice?
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#8c8c8c",
                marginBottom: "24px",
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
              }}
            >
              This action will create a new sale record and cannot be undone.
            </div>
            <div
              style={{ display: "flex", gap: "12px", justifyContent: "center" }}
            >
              <Button
                onClick={() => {
                  setShowConfirmationModal(false);
                  setSaleFormData(null);
                }}
                style={{ minWidth: "100px" }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmSale}
                loading={isLoading}
                style={{ minWidth: "100px" }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SaleForm;
